import React, { useMemo } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import {
  PurchaseOrderFormValues,
  PurchaseOrderItem
} from 'src/redux/slices/purchase-order/types';
import { getDrjInfoViaID } from 'src/redux/slices/branches/util';
import { formatCurrency } from 'src/utils';
import { colors } from 'src/constants';
import { getVatIncValue } from 'src/utils/vatUtils';

interface Props {
  purchaseOrderData: PurchaseOrderFormValues;
}

const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    padding: 30,
    fontFamily: 'Helvetica'
  },
  logo: {
    width: 180,
    height: 60,
    marginBottom: 10
  },
  barcodeView: {
    textAlign: 'left',
    width: 200
  },
  barcode: {
    height: 180,
    width: 200,
    position: 'absolute',
    top: 13
  },
  sectionHeader: {
    fontSize: 12,
    marginBottom: 8,
    fontFamily: 'Helvetica-Bold'
  },
  title: {
    fontSize: 16,
    marginVertical: 10
  },
  flexCol: {
    display: 'flex',
    flexDirection: 'column'
  },
  flexRowContainer: {
    display: 'flex',
    flexDirection: 'row-reverse'
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  bold: {
    fontFamily: 'Helvetica-Bold'
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottom: '1px solid #000',
    marginTop: 10,
    paddingBottom: 4
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    borderBottom: '1px solid #eee'
  },
  cell: {
    flex: 1,
    paddingRight: 4
  },
  rightAlign: {
    textAlign: 'right'
  },
  deliverySection: {
    marginTop: 30,
    paddingTop: 10,
    borderTop: '1px solid #000'
  },
  footerText: {
    fontSize: 8,
    textAlign: 'right',
    marginTop: 20
  }
});

const currencyStyles = StyleSheet.create({
  text: {
    fontSize: 8,
    maxLines: 1
  },
  image: {
    height: 6,
    width: 6,
    marginTop: 2,
    marginHorizontal: 2
  },
  container: {
    flexDirection: 'row'
  }
});

const maxProductsPerPage = 8;

/**
 * NOTE: TALAGANG MAY MALAKING SPACING YUNG PDF KAPAG NAG PRINT. PARA PAG NAGMAXIMUM YUNG LINES NG PRODUCTS. KASYA PARIN!!!
 */

const PurchaseOrderPDFDocument = ({ purchaseOrderData = {} }: Props) => {
  const siPercentage = useMemo(() => purchaseOrderData.si_percentage, [
    purchaseOrderData
  ]);

  const totalPages = useMemo(() => {
    const allProducts = purchaseOrderData?.products || [];
    return Math.ceil(allProducts?.length / maxProductsPerPage); // Assuming 10 products per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderData?.products]);

  const paginatedPurchaseOrder = useMemo(() => {
    const products = purchaseOrderData?.products || [];
    const pages = [];
    for (let i = 0; i < products.length; i += maxProductsPerPage) {
      const paginatedProducts = products.slice(i, i + maxProductsPerPage);
      pages.push({
        pageNo: pages.length + 1,
        page: {
          ...purchaseOrderData,
          products: paginatedProducts
        }
      });
    }
    return pages;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderData?.products]);

  const RenderCurrency = ({
    amount,
    size = 6
  }: {
    amount: number;
    size?: number;
  }) => {
    return (
      <View style={[currencyStyles.container]}>
        <Image
          style={[
            currencyStyles.image,
            { height: size, width: size, paddingRight: 2 }
          ]}
          src="/static/images/icons/ico-php.png"
        />
        <Text>
          {`${formatCurrency(amount, '', 2, { removeCurrency: true })}`}
        </Text>
      </View>
    );
  };

  const Header = () => {
    return (
      <View style={styles.flexCol}>
        <View style={styles.flexRow}>
          <View>
            <Image style={styles.logo} src="/static/new_drj_logo.png" />
          </View>
          <View style={{ textAlign: 'right' }}>
            <Text>
              <Text style={styles.bold}>Purchase Order Date:</Text>{' '}
              {purchaseOrderData?.po_date}
            </Text>
            <Text>
              <Text style={styles.bold}>Delivery Date: </Text>{' '}
              {purchaseOrderData?.delivery_date}
            </Text>
            <Text>
              <Text style={styles.bold}>Purchase Order Number: </Text>
              {purchaseOrderData?.order_no || 'N/A'}
            </Text>
            <Text>
              <Text style={styles.bold}>Supplier: </Text>
              {purchaseOrderData?.supplier?.name || 'N/A'}
            </Text>
            {purchaseOrderData?.po_no_barcode ? (
              <View style={styles.barcodeView}>
                <Text style={styles.bold}>Barcode:</Text>
                <Image
                  style={styles.barcode}
                  src={purchaseOrderData?.po_no_barcode}
                />
              </View>
            ) : null}
          </View>
        </View>
      </View>
    );
  };

  const PurchaseOrderInfo = () => {
    return (
      <View style={[styles.flexRow, { justifyContent: 'flex-start' }]}>
        <View>
          <Text style={styles.bold}>Prepared By: </Text>
          <Text>{purchaseOrderData?.created_by || 'PC Worth'} </Text>
        </View>
        <View style={{ marginLeft: 20 }}>
          <Text style={styles.bold}>Status: </Text>
          <Text>{purchaseOrderData?.status}</Text>
        </View>
      </View>
    );
  };

  const TableHeader = () => {
    return (
      <View style={styles.tableHeader}>
        <Text style={[styles.cell, styles.bold, { flex: 0.5 }]}>QTY</Text>
        <Text style={[styles.cell, styles.bold, { flex: 5 }]}>Description</Text>
        <Text style={[styles.cell, styles.bold, { flex: 0.75 }]}>
          Unit Price
        </Text>
        <Text
          style={[styles.cell, styles.bold, { flex: 1 }, styles.rightAlign]}
        >
          Total
        </Text>
      </View>
    );
  };

  const TableRow = ({ product }: { product: PurchaseOrderItem }) => {
    const dealer_price = product?.dealer_price || 0;

    const rowTotalAmount = dealer_price * (product?.quantity || 0);
    return (
      <View style={styles.tableRow}>
        <Text style={[styles.cell, { flex: 0.5, fontSize: 9 }]}>
          {product?.quantity || 0}
        </Text>
        <Text
          style={[
            styles.cell,
            {
              flex: 5,
              fontSize: 9,
              maxLines: 4,
              textOverflow: 'ellipsis'
            }
          ]}
        >
          {product?.sku ? (
            <Text
              style={[{ fontSize: 7, color: colors.primary }, styles.bold]}
            >{`(${product?.sku || 'SKU N/A'}) `}</Text>
          ) : null}

          {product?.product_name || 'Product Name N/A'}
        </Text>
        <Text style={[styles.cell, { flex: 0.75, fontSize: 9 }]}>
          <RenderCurrency
            amount={
              siPercentage
                ? getVatIncValue(dealer_price || 0, siPercentage)
                : dealer_price
            }
          />
        </Text>
        <Text
          style={[styles.cell, { flex: 1, fontSize: 9 }, styles.rightAlign]}
        >
          <RenderCurrency
            amount={
              siPercentage
                ? getVatIncValue(rowTotalAmount, siPercentage)
                : rowTotalAmount
            }
          />
        </Text>
      </View>
    );
  };

  const totalAmount = useMemo(() => {
    const total = purchaseOrderData?.products?.reduce(
      (acc: number, product: PurchaseOrderItem) => {
        const total = (product?.dealer_price || 0) * (product?.quantity || 0);
        return acc + total;
      },
      0
    );
    return siPercentage ? getVatIncValue(total ?? 0, siPercentage ?? 0) : total;
  }, [purchaseOrderData, siPercentage]);

  const DeliveryTotal = () => {
    return (
      <Text
        style={[
          styles.bold,
          { fontSize: 12, marginTop: 10, textAlign: 'right' }
        ]}
      >
        <RenderCurrency amount={totalAmount || 0} size={9} />
      </Text>
    );
  };

  const DeliveryDetails = () => {
    const branch = purchaseOrderData?.branch;
    const branchInfo = getDrjInfoViaID(branch?.id);

    return (
      <View style={styles.deliverySection}>
        <Text style={styles.sectionHeader}>Delivery Details</Text>
        <Text>Branch: {branch?.name || 'N/A'} </Text>
        <Text>Delivery Address: {branchInfo?.location || 'N/A'}</Text>
        {/* <Text>Attention: Gaille Callejo</Text>
          <Text>Contact Number: +639276608546</Text> */}
        <Text style={{ marginTop: 2 }}>
          Remarks: {purchaseOrderData?.remarks}
        </Text>
      </View>
    );
  };

  const RenderPages = () => {
    return paginatedPurchaseOrder.map((pageData, i) => (
      <Page key={i} size="A4" style={styles.page}>
        {/* Header */}
        <Header />

        {/* Title */}
        <Text style={[styles.bold, styles.title]}>PURCHASE ORDER</Text>

        {/* Info */}
        <PurchaseOrderInfo />

        {/* Table Header */}
        <TableHeader />

        {/* Table Rows */}
        {pageData?.page?.products?.map((product) => (
          <TableRow product={product} key={product.product_id} />
        ))}

        {/* Totals */}
        <DeliveryTotal />

        {/* Delivery Details */}
        <DeliveryDetails />

        {/* Footer */}
        <Text style={styles.footerText}>
          Page {pageData?.pageNo} of {totalPages}
        </Text>
      </Page>
    ));
  };

  return <Document>{RenderPages()}</Document>;
};

export default PurchaseOrderPDFDocument;
